// src/features/privateAlbum/components/PhotoGrid.jsx

import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Zoom,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  ImageNotSupported as ImageNotSupportedIcon,
} from "@mui/icons-material";
import { formatFileSize } from "../utils/privateAlbumHelpers";

const PhotoGrid = ({
  photos,
  onPhotoDelete,
  onPhotoUpdate,
  onSetCover,
  allowEdit = false,
  allowDownload = true,
}) => {
  const [viewPhoto, setViewPhoto] = useState(null);
  const [editingCaption, setEditingCaption] = useState(null);
  const [brokenImages, setBrokenImages] = useState({});

  if (!photos || photos.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No photos in this album
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Upload photos to get started
        </Typography>
      </Box>
    );
  }

  const handlePhotoClick = (photo) => {
    setViewPhoto(photo);
  };

  const handleDelete = async (photo, e) => {
    e.stopPropagation();
    if (
      window.confirm(`Are you sure you want to delete "${photo.fileName}"?`)
    ) {
      await onPhotoDelete(photo.id);
    }
  };

  const handleSetCover = async (photo, e) => {
    e.stopPropagation();
    if (window.confirm(`Set "${photo.fileName}" as cover photo?`)) {
      await onSetCover(photo.albumId, photo.id);
    }
  };

  const handleCaptionChange = async (photo, newCaption) => {
    await onPhotoUpdate(photo.id, { caption: newCaption });
    setEditingCaption(null);
  };

  const handleImageError = (photoId) => {
    setBrokenImages((prev) => ({ ...prev, [photoId]: true }));
  };

  const isValidUrl = (url) => {
    if (!url) return false;
    // Check if it's a blob URL - blob URLs are only valid in the same session
    if (url.startsWith("blob:")) {
      // Check if the blob URL is still valid by trying to fetch it
      try {
        // We'll let the image onError handle this
        return true; // Let the image try to load
      } catch {
        return false;
      }
    }
    // Check if it's a valid http/https URL
    return (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("data:")
    );
  };

  // Check if photo URL is valid
  const isPhotoUrlValid = (photo) => {
    if (brokenImages[photo.id]) return false;
    return isValidUrl(photo.url);
  };

  return (
    <>
      {/* Photo Grid */}
      <Grid container spacing={2}>
        {photos.map((photo) => {
          const isBroken = brokenImages[photo.id];
          const imageUrl = photo.thumbnailUrl || photo.url;

          return (
            <Grid item xs={6} sm={4} md={3} key={photo.id}>
              <Card
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: 4,
                  },
                }}
                onClick={() => handlePhotoClick(photo)}
              >
                <Box
                  sx={{ position: "relative", pt: "100%", bgcolor: "grey.100" }}
                >
                  {!isBroken ? (
                    <CardMedia
                      component="img"
                      image={imageUrl}
                      alt={photo.caption || photo.fileName}
                      onError={() => handleImageError(photo.id)}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.200",
                        flexDirection: "column",
                      }}
                    >
                      <ImageNotSupportedIcon
                        sx={{ fontSize: 48, color: "grey.500" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Image unavailable
                      </Typography>
                    </Box>
                  )}

                  {/* Overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.3s",
                      "&:hover": { opacity: 1 },
                    }}
                  >
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View">
                        <IconButton
                          sx={{
                            bgcolor: "white",
                            "&:hover": { bgcolor: "grey.200" },
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {allowEdit && (
                        <>
                          <Tooltip title="Set as cover">
                            <IconButton
                              sx={{
                                bgcolor: "success.light",
                                "&:hover": { bgcolor: "success.main" },
                              }}
                              onClick={(e) => handleSetCover(photo, e)}
                            >
                              <StarIcon sx={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              sx={{
                                bgcolor: "error.light",
                                "&:hover": { bgcolor: "error.main" },
                              }}
                              onClick={(e) => handleDelete(photo, e)}
                            >
                              <DeleteIcon sx={{ color: "white" }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Box>

                  {/* Cover Badge */}
                  {photo.isCover && (
                    <Chip
                      label="Cover"
                      size="small"
                      color="warning"
                      sx={{ position: "absolute", top: 8, left: 8 }}
                    />
                  )}
                </Box>

                {/* Photo Info */}
                <CardContent sx={{ py: 1 }}>
                  {allowEdit && editingCaption === photo.id ? (
                    <TextField
                      fullWidth
                      size="small"
                      defaultValue={photo.caption || ""}
                      onBlur={(e) => handleCaptionChange(photo, e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleCaptionChange(photo, e.target.value);
                        }
                      }}
                      autoFocus
                      placeholder="Add caption..."
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem" } }}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      noWrap
                      onClick={(e) => {
                        e.stopPropagation();
                        if (allowEdit) setEditingCaption(photo.id);
                      }}
                      sx={{ cursor: allowEdit ? "pointer" : "default" }}
                    >
                      {photo.caption || photo.fileName}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Photo View Dialog */}
      <Dialog
        open={!!viewPhoto}
        onClose={() => setViewPhoto(null)}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Zoom}
      >
        {viewPhoto && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" noWrap sx={{ maxWidth: "80%" }}>
                {viewPhoto.caption || viewPhoto.fileName}
              </Typography>
              <IconButton onClick={() => setViewPhoto(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {!brokenImages[viewPhoto.id] ? (
                <Box
                  component="img"
                  src={viewPhoto.url}
                  alt={viewPhoto.caption || viewPhoto.fileName}
                  onError={() => handleImageError(viewPhoto.id)}
                  sx={{
                    width: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.100",
                    flexDirection: "column",
                    borderRadius: 1,
                  }}
                >
                  <ImageNotSupportedIcon
                    sx={{ fontSize: 64, color: "grey.400" }}
                  />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Image not available
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The image file may have been moved or deleted
                  </Typography>
                </Box>
              )}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>File Name:</strong> {viewPhoto.fileName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Size:</strong> {formatFileSize(viewPhoto.fileSize)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Dimensions:</strong> {viewPhoto.width} ×{" "}
                    {viewPhoto.height}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Views:</strong> {viewPhoto.views || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Likes:</strong> {viewPhoto.likes || 0}
                  </Typography>
                  {viewPhoto.isCover && (
                    <Chip
                      label="⭐ Cover Photo"
                      size="small"
                      color="warning"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            {allowDownload && !brokenImages[viewPhoto.id] && (
              <DialogActions>
                <Button
                  component="a"
                  href={viewPhoto.url}
                  download={viewPhoto.fileName}
                  variant="contained"
                  startIcon={<DownloadIcon />}
                >
                  Download
                </Button>
              </DialogActions>
            )}
          </>
        )}
      </Dialog>
    </>
  );
};

export default PhotoGrid;
